import { gql } from '@apollo/client';
import axios from 'axios';
import { apolloClient } from '../apollo/client';
import { dispatchGlobalError } from '../context/ApiErrorContext';
import { dispatchLogout } from '../context/AuthContext';

export interface PatrocinadorTorneio {
  id: string;
  nome: string;
  torneioId: string;
  link?: string | null;
  descricao?: string | null;
  fotoUrl?: string | null;
}

export interface PatrocinadoresPorTorneioData {
  patrocinadoresPorTorneio: PatrocinadorTorneio[];
}

export interface PatrocinadoresPorTorneioVars {
  torneioId: string;
}

export const PATROCINADORES_POR_TORNEIO = gql`
  query PatrocinadoresPorTorneio($torneioId: ID!) {
    patrocinadoresPorTorneio(torneioId: $torneioId) {
      id
      nome
      link
      descricao
      fotoUrl
    }
  }
`;

export interface PatrocinadorTorneioInput {
  torneioId: string;
  nome: string;
  link?: string | null;
  descricao?: string | null;
  fotoUrl?: string | null;
}

export const CRIAR_PATROCINADOR_TORNEIO = gql`
  mutation CriarPatrocinadorTorneio($input: PatrocinadorTorneioInput!) {
    adicionarPatrocinadorTorneio(input: $input) {
      id
      nome
      link
      descricao
      fotoUrl
    }
  }
`;

export interface AtualizarPatrocinadorTorneioVars {
  id: string;
  input: {
    torneioId: string;
    nome?: string;
    link?: string | null;
    descricao?: string | null;
    fotoUrl?: string | null;
  };
}

export const ATUALIZAR_PATROCINADOR_TORNEIO = gql`
  mutation AtualizarPatrocinadorTorneio($id: ID!, $input: PatrocinadorTorneioInput!) {
    atualizarPatrocinadorTorneio(id: $id, input: $input) {
      id
      nome
      link
      descricao
      fotoUrl
    }
  }
`;

export const DELETAR_PATROCINADOR_TORNEIO = gql`
  mutation RemoverPatrocinadorTorneio($id: ID!) {
    removerPatrocinadorTorneio(id: $id)
  }
`;

export const UPLOAD_FOTO_PATROCINADOR = gql`
  mutation UploadFotoPatrocinador($id: ID!, $file: Upload!) {
    uploadFotoPatrocinador(id: $id, file: $file) {
      fotoUrl
    }
  }
`;

export const patrocinadoresPorTorneio = async (
  torneioId: string,
): Promise<PatrocinadorTorneio[]> => {
  const { data } = await apolloClient.query<
    PatrocinadoresPorTorneioData,
    PatrocinadoresPorTorneioVars
  >({
    query: PATROCINADORES_POR_TORNEIO,
    variables: { torneioId },
    fetchPolicy: 'no-cache',
  });
  return data.patrocinadoresPorTorneio;
};

export const criarPatrocinadorTorneio = async (
  input: PatrocinadorTorneioInput,
): Promise<PatrocinadorTorneio | null> => {
  const { data } = await apolloClient.mutate<
    { adicionarPatrocinadorTorneio: PatrocinadorTorneio },
    { input: PatrocinadorTorneioInput }
  >({
    mutation: CRIAR_PATROCINADOR_TORNEIO,
    variables: { input },
  });
  return data?.adicionarPatrocinadorTorneio ?? null;
};

export const atualizarPatrocinadorTorneio = async (
  vars: AtualizarPatrocinadorTorneioVars,
): Promise<PatrocinadorTorneio | null> => {
  const { data } = await apolloClient.mutate<
    { atualizarPatrocinadorTorneio: PatrocinadorTorneio },
    AtualizarPatrocinadorTorneioVars
  >({
    mutation: ATUALIZAR_PATROCINADOR_TORNEIO,
    variables: vars,
  });
  return data?.atualizarPatrocinadorTorneio ?? null;
};

export const removerPatrocinadorTorneio = async (
  id: string,
): Promise<boolean> => {
  const { data } = await apolloClient.mutate<
    { removerPatrocinadorTorneio: boolean },
    { id: string }
  >({
    mutation: DELETAR_PATROCINADOR_TORNEIO,
    variables: { id },
  });
  return data?.removerPatrocinadorTorneio ?? false;
};

const restUrl = process.env.REACT_APP_GRAPHQL_ENDPOINT + 'api/';

export const uploadFotoPatrocinador = async (
  id: string,
  arquivo: File,
): Promise<{ fotoUrl: string }> => {
  const formData = new FormData();
  formData.append('arquivo', arquivo);

  const token = localStorage.getItem('token');
  const headers: Record<string, string> = { 'Content-Type': 'multipart/form-data' };
  if (token) {
    headers['authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await axios.post<string>(
      `${restUrl}patrocinadores/${id}/foto`,
      formData,
      { headers },
    );
    return { fotoUrl: response.data ?? '' };
  } catch (error: any) {
    if (error.response?.status === 401) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
    let message = 'Erro inesperado. Tente novamente.';
    if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
};

